import clsx from 'clsx';
import React, { useMemo } from 'react';
import { SalesforceProductSpecialPrice } from '~/lib/salesforce/types';
import { useSpecialPrice } from '~/services/special-price/useSpecialPrice';
import { TNotification } from '~/shared/components/ProductCard/components/Notifications';
import { TPrice } from '~/shared/components/ProductCard/components/Price';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import styles from './ProductCampaign.module.scss';

interface ProductCampaignProps {
    id: string;
    notifications: TNotification[] | undefined;
    specialPrices: SalesforceProductSpecialPrice | undefined;
    prices: TPrice[] | undefined;
}

export const extractProductCampaign = (product: TProductDetails): ProductCampaignProps => ({
    id: product.id,
    notifications: product.notifications,
    specialPrices: product.cust_specialPrices,
    prices: product.c_prices ?? product.cust_prices,
});

type Props = ProductCampaignProps & React.HTMLAttributes<HTMLDivElement>;

export const ProductCampaign: React.FC<Props> = ({
    className,
    notifications,
    specialPrices,
    prices,
    id,
    ...rest
}) => {
    const { campaignText } = useSpecialPrice({
        id,
        c_specialPrices: specialPrices,
        c_prices: prices,
    });

    const content = useMemo(() => {
        // Special pricing campaign text takes precedence over other texts.
        if (campaignText) return <p className={styles.campaignText}>{campaignText}</p>;

        if (notifications?.length) {
            return (
                <p className={styles.notifications}>
                    {notifications.map(({ text, type }) => (
                        <span key={type} className={styles.notificationText}>
                            {text}
                        </span>
                    ))}
                </p>
            );
        }

        return null;
    }, [notifications, campaignText]);

    return (
        <div className={clsx(styles.campaign, className)} {...rest}>
            {content}
        </div>
    );
};
